// What a timeline step changed in the model, and what it does to the database. Shown in the
// inspector while a timeline is active and nothing is selected.
import { describeOperation, type ModelSpec } from '@modelviewer/core';
import { FileCode2, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { ChangeList } from '../diff/ChangeList';
import { cn } from '../lib/cn';
import { openSourceFile } from '../state/dock';
import { revealEntity } from '../state/workspace';
import { stepDate } from './Scrubber';
import type { Timeline, TimelineStep } from './model';

function SourceButton({ path, line, label }: { path: string; line?: number; label?: string }) {
  return (
    <button onClick={() => openSourceFile(path, line)} className="flex min-w-0 items-center gap-1.5 text-[12px] text-accent hover:underline" title={path}>
      <FileCode2 size={13} className="flex-none" />
      <span className="truncate">{label ?? path.slice(path.lastIndexOf('/') + 1)}</span>
    </button>
  );
}

function ModelChanges({ step, before, onReveal }: { step: TimelineStep; before: ModelSpec; onReveal: (key: string, removed: boolean) => void }) {
  if (!step.model || !step.diff) return <p className="px-3 py-3 text-[12.5px] text-muted">{step.error ?? 'The model after this migration is unknown.'}</p>;
  return (
    <ChangeList
      changes={step.diff.changes}
      after={step.model}
      before={before}
      onReveal={onReveal}
      empty="No changes to the logical model. See the database operations for what this migration does."
    />
  );
}

function DatabaseOperations({ step }: { step: TimelineStep }) {
  const main = step.migration?.mainPath;
  if (!step.operations || !main) return <p className="px-3 py-3 text-[12.5px] text-muted">{step.drift ? 'A snapshot has no database operations.' : 'The migration file could not be read.'}</p>;
  if (step.operations.up.length === 0) return <p className="px-3 py-3 text-[12.5px] text-muted">This migration does nothing to the database.</p>;
  return (
    <ul className="px-1.5 py-1.5" data-testid="database-operations">
      {step.operations.up.map((op, i) => {
        const d = describeOperation(op);
        return (
          <li key={i}>
            <button onClick={() => openSourceFile(main, op.line)} className="flex w-full flex-col rounded-md px-1.5 py-1 text-left hover:bg-hover" title={`${op.kind}, line ${op.line}`}>
              <span className="flex items-center gap-1.5 text-[12.5px]">
                {d.flag && <TriangleAlert size={12} className="flex-none text-changed" aria-label={d.flag} />}
                <span className="min-w-0 truncate">{d.text}</span>
                <span className="ml-auto flex-none text-[11px] text-faint">{op.line}</span>
              </span>
              {d.detail && <span className="truncate font-mono text-[11px] text-muted">{d.detail}</span>}
              {d.flag && <span className="text-[11.5px] text-changed">{d.flag}</span>}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function ChangePanel({ timeline, index }: { timeline: Timeline; index: number }) {
  const [tab, setTab] = useState<'model' | 'database'>('model');
  const step = timeline.steps[index]!;
  const before = step.previous !== null ? timeline.steps[step.previous]!.model! : { entities: {}, relationships: [] };
  const lastMigration = step.previous !== null ? timeline.steps[step.previous]! : null;
  const custom = step.operations?.customCode.filter((c) => c.direction === 'up') ?? [];
  const opCount = step.operations?.up.length ?? 0;
  const changes = step.diff?.changes.length ?? 0;
  const migrationIndex = timeline.steps.filter((s) => s.migration).indexOf(step);
  const onReveal = (key: string, removed: boolean) => {
    const ref = removed ? lastMigration?.ref : step.ref;
    if (ref) revealEntity(ref, key);
  };

  return (
    <section className="flex min-h-full flex-col" aria-label="Changes">
      <header className="flex-none border-b border-border px-3 py-3">
        <p className="text-[11px] font-semibold tracking-wider text-faint uppercase">
          {step.migration ? `Migration ${migrationIndex + 1} of ${timeline.steps.filter((s) => s.migration).length}` : 'Snapshot'}
        </p>
        <h2 className="mt-0.5 truncate text-[14px] font-semibold" title={step.id}>
          {step.name}
        </h2>
        <p className="text-[12px] text-muted">{step.timestamp ? `${stepDate(step)} · ${step.id.slice(0, 14)}` : step.file?.slice(step.file.lastIndexOf('/') + 1)}</p>
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
          {step.migration?.mainPath && <SourceButton path={step.migration.mainPath} label="Migration" />}
          {step.file && <SourceButton path={step.file} label={step.migration ? 'Designer' : 'Snapshot'} />}
        </div>
      </header>

      <div className="flex-1">
        {step.drift && (
          <div className="m-3 rounded-md border border-changed/40 bg-changed-soft p-2.5 text-[12px] leading-relaxed" role="note">
            <p className="flex items-center gap-1.5 font-semibold text-changed">
              <TriangleAlert size={13} /> The snapshot differs from the last migration
            </p>
            <p className="mt-1 text-text">
              The model snapshot does not match the model of {lastMigration?.name ?? 'the last migration'}. This usually means a migration was deleted or edited by hand, or a merge combined two
              branches. The next migration is computed from the snapshot, so it may miss or repeat these changes.
            </p>
          </div>
        )}
        {custom.length > 0 && (
          <div className="m-3 rounded-md border border-changed/40 bg-changed-soft p-2.5 text-[12px]" role="note">
            <p className="flex items-center gap-1.5 font-semibold text-changed">
              <TriangleAlert size={13} /> Hand-written code
            </p>
            <p className="mt-1 text-text">This migration does things the model cannot show:</p>
            <ul className="mt-1">
              {custom.map((c, i) => (
                <li key={i}>
                  <button onClick={() => step.migration?.mainPath && openSourceFile(step.migration.mainPath, c.line)} className="text-left text-accent hover:underline">
                    Line {c.line}: {c.reason}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="sticky top-0 z-10 flex gap-1 border-b border-border bg-panel px-3 pt-1" role="tablist">
          {(
            [
              ['model', 'Model', changes],
              ['database', 'Database', opCount],
            ] as const
          ).map(([value, label, count]) => (
            <button
              key={value}
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn('-mb-px border-b-2 px-2 py-1.5 text-[12.5px]', tab === value ? 'border-accent text-text' : 'border-transparent text-muted hover:text-text')}
            >
              {label} <span className="text-[11px] text-faint">{count}</span>
            </button>
          ))}
        </div>
        {tab === 'model' ? <ModelChanges step={step} before={before} onReveal={onReveal} /> : <DatabaseOperations step={step} />}
      </div>
    </section>
  );
}
